import React, { useEffect } from "react";
import styled from "styled-components";
import { useTranslation, Trans } from "react-i18next";
import { HashLink } from "react-router-hash-link";
import {
  ToolsIcon,
  RocketIcon,
  LockIcon,
  UsersIcon,
  CheckCircleIcon,
  DatabaseIcon,
  CloudIcon,
  CodeIcon,
  DesktopIcon,
  MobileAltIcon,
  LightbulbIcon,
} from "./IconComponents";
import SEO from "./SEO";

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 2rem 0;
  background-color: var(--light-background);
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  color: var(--text-color);
`;

const HeroSection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 6rem 0 4rem;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto 2rem;
  line-height: 1.6;
`;

const ContentSection = styled.section`
  padding: 4rem 0;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const SectionTitleQuestion = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 0rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const Card = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: white;
  font-size: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CardDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
`;

const ProcessSection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ProcessCard = styled.div`
  text-align: center;
  padding: 2rem;
`;

const ProcessNumber = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 auto 1.5rem;
`;

const ProcessTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ProcessDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const BenefitsSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin: 3rem 0;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--card-background);
  border-radius: 8px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
`;

const BenefitIcon = styled.div`
  color: var(--primary-color);
  font-size: 1.5rem;
  margin-top: 0.2rem;
  flex-shrink: 0;
`;

const BenefitText = styled.div`
  h5 {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    color: var(--text-color);
  }

  p {
    color: var(--text-color);
    line-height: 1.5;
    font-size: 0.95rem;
  }
`;

const TechnologySection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const TechnologyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const TechCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  border-radius: 8px;
  background: var(--light-background);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const TechIcon = styled.div`
  color: var(--primary-color);
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const TechName = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
`;

const TechDescription = styled.p`
  color: var(--text-color);
  font-size: 0.9rem;
  line-height: 1.4;
`;

const CaseStudySection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const CaseStudyCard = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--primary-color),
      var(--secondary-color)
    );
  }
`;

const CaseStudyTitle = styled.h4`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CaseStudyText = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const CaseStudyResult = styled.div`
  background: var(--light-background);
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid var(--primary-color);

  h5 {
    color: var(--primary-color);
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }

  p {
    color: var(--text-color);
    font-size: 0.9rem;
    margin: 0;
  }
`;

const ServiceTypesSection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const ServiceTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ServiceTypeCard = styled.div`
  background: var(--light-background);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  position: relative;
  border: 2px solid transparent;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    transform: translateY(-5px);
  }
`;

const ServiceTypeTitle = styled.h4`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ServiceTypeDescription = styled.p`
  color: var(--text-color);
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ServiceTypeFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    color: var(--text-color);
    padding: 0.5rem 0;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:before {
      content: "✓";
      color: var(--primary-color);
      font-weight: bold;
    }
  }
`;

const FAQSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background: var(--card-background);
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FAQQuestion = styled.h4`
  padding: 1.5rem;
  margin: 0;
  background: var(--primary-color);
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: var(--secondary-color);
  }
`;

const FAQAnswer = styled.p`
  padding: 1.5rem;
  margin: 0;
  color: var(--text-color);
  line-height: 1.6;
`;

const CTASection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 4rem 0;
  text-align: center;
`;

const CTATitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const CTAContainer = styled.div`
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  padding: 3rem;
  background-color: var(--card-background);
  border-radius: 8px;
`;

const CTAButton = styled(HashLink)`
  display: block;
  width: 50%;
  margin: 2rem auto 0;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 4px;
  background-color: var(--primary-color);
  color: var(--light-text-color);
  font-size: 1.05rem;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background-color: var(--secondary-color);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid var(--primary-color);
    outline-offset: 4px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Automatisierung: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const advantages = [
    {
      icon: <RocketIcon />,
      title: "Dramatische Effizienzsteigerung",
      description:
        "Durch Automatisierung wiederkehrender Aufgaben können Sie die Produktivität um bis zu 70% steigern und Ihre Mitarbeiter für wertschöpfende Tätigkeiten freisetzen.",
    },
    {
      icon: <LockIcon />,
      title: "Fehlerreduktion & Qualität",
      description:
        "Automatisierte Prozesse eliminieren menschliche Fehler und sorgen für konsistente, zuverlässige Ergebnisse in höchster Qualität.",
    },
    {
      icon: <UsersIcon />,
      title: "Mitarbeiterzufriedenheit",
      description:
        "Befreien Sie Ihr Team von monotonen Routineaufgaben und ermöglichen Sie es ihnen, sich auf kreative und strategische Aufgaben zu konzentrieren.",
    },
    {
      icon: <LightbulbIcon />,
      title: "24/7 Verfügbarkeit",
      description:
        "Automatisierte Systeme arbeiten rund um die Uhr ohne Pausen, Urlaub oder Krankheitstage und gewährleisten kontinuierliche Geschäftsabläufe.",
    },
  ];

  const process = [
    {
      title: "Prozessanalyse",
      description:
        "Wir analysieren Ihre bestehenden Geschäftsprozesse und identifizieren Automatisierungspotenziale mit dem höchsten ROI.",
    },
    {
      title: "Lösungsdesign",
      description:
        "Entwicklung einer maßgeschneiderten Automatisierungsstrategie mit klaren Prioritäten und messbaren Zielen.",
    },
    {
      title: "Implementierung",
      description:
        "Schrittweise Umsetzung der Automatisierungslösungen mit minimaler Unterbrechung Ihrer laufenden Geschäfte.",
    },
    {
      title: "Optimierung",
      description:
        "Kontinuierliche Überwachung und Optimierung der automatisierten Prozesse für maximale Effizienz.",
    },
  ];

  const benefits = [
    {
      title: "Kosteneinsparungen",
      description:
        "Reduzierung der Betriebskosten um bis zu 60% durch Eliminierung manueller Arbeitsschritte und Minimierung von Fehlern.",
    },
    {
      title: "Schnellere Durchlaufzeiten",
      description:
        "Verkürzung der Bearbeitungszeiten um durchschnittlich 80% durch nahtlose, automatisierte Workflows.",
    },
    {
      title: "Skalierbarkeit",
      description:
        "Automatisierte Systeme können problemlos mit Ihrem Unternehmenswachstum skaliert werden ohne proportionalen Personalmehraufwand.",
    },
    {
      title: "Compliance & Nachverfolgbarkeit",
      description:
        "Automatische Dokumentation und Protokollierung aller Prozessschritte für perfekte Compliance und Auditierbarkeit.",
    },
    {
      title: "Datenqualität & Insights",
      description:
        "Konsistente Datenerfassung und -verarbeitung liefert zuverlässige Geschäftsintelligenz für bessere Entscheidungen.",
    },
    {
      title: "Wettbewerbsvorteile",
      description:
        "Durch schnellere, effizientere Prozesse können Sie Ihren Wettbewerbern einen entscheidenden Schritt voraus sein.",
    },
  ];

  const technologies = [
    {
      icon: <ToolsIcon />,
      name: "RPA Tools",
      description: "UiPath, Automation Anywhere, Blue Prism",
    },
    {
      icon: <CloudIcon />,
      name: "Cloud Automation",
      description: "Microsoft Power Automate, Azure Logic Apps",
    },
    {
      icon: <CodeIcon />,
      name: "Custom Scripts",
      description: "Python, PowerShell, .NET Automation",
    },
    {
      icon: <DatabaseIcon />,
      name: "Datenintegration",
      description: "ETL Tools, API-Integration, Datenbanken",
    },
    {
      icon: <DesktopIcon />,
      name: "Desktop Automation",
      description: "Windows Workflow, Task Scheduler",
    },
    {
      icon: <MobileAltIcon />,
      name: "Mobile Integration",
      description: "App-basierte Workflows, Push Notifications",
    },
  ];

  const caseStudies = [
    {
      title: "Rechnungsverarbeitung im Großhandel",
      description:
        "Vollautomatisierung der Rechnungsverarbeitung eines Großhandelsunternehmens mit 500+ Lieferanten durch OCR, KI-gestützte Validierung und ERP-Integration.",
      result: {
        title: "Ergebnisse:",
        description:
          "95% Reduzierung der manuellen Arbeit, 75% schnellere Rechnungsbearbeitung, 99.8% Genauigkeitsrate, ROI von 300% im ersten Jahr",
      },
    },
    {
      title: "HR-Prozesse in der Personalverwaltung",
      description:
        "Automatisierung der Mitarbeiteronboarding-Prozesse, Urlaubsanträge, Zeiterfassung und Gehaltsabrechnungsvorbereitung für ein 200-Mitarbeiter Unternehmen.",
      result: {
        title: "Ergebnisse:",
        description:
          "85% weniger Verwaltungsaufwand, 90% kürzere Onboarding-Zeit, vollständige Compliance-Dokumentation, erhöhte Mitarbeiterzufriedenheit",
      },
    },
    {
      title: "Kundenservice-Automatisierung",
      description:
        "Implementierung eines intelligenten Ticketing-Systems mit automatischer Kategorisierung, Routing und Erstbearbeitung häufiger Kundenanfragen.",
      result: {
        title: "Ergebnisse:",
        description:
          "60% weniger Support-Tickets für Mitarbeiter, 40% schnellere Lösungszeiten, 35% höhere Kundenzufriedenheit, 24/7 Verfügbarkeit",
      },
    },
  ];

  const serviceTypes = [
    {
      title: "Prozessautomatisierung",
      description:
        "Vollautomatisierung wiederkehrender Geschäftsprozesse von der Datenerfassung bis zur Ausgabe.",
      features: [
        "Workflow-Design und -Implementierung",
        "Integration bestehender Systeme",
        "Automatische Datenvalidierung",
        "Exception Handling",
        "Performance Monitoring",
      ],
    },
    {
      title: "Dokumentenautomatisierung",
      description:
        "Intelligente Verarbeitung und Klassifizierung von Dokumenten mit OCR und KI-Technologien.",
      features: [
        "OCR und Dokumentenerkennung",
        "Automatische Klassifizierung",
        "Datenextraktion und -validierung",
        "Archivierung und Indexierung",
        "Compliance-konforme Verwaltung",
      ],
    },
    {
      title: "Kommunikationsautomatisierung",
      description:
        "Automatisierung von E-Mail-Verkehr, Benachrichtigungen und Kundenkommunikation.",
      features: [
        "E-Mail-Automation und Templates",
        "SMS und Push-Benachrichtigungen",
        "Chatbot-Integration",
        "Personalisierte Kommunikation",
        "Multi-Channel Orchestrierung",
      ],
    },
  ];

  const faqs = [
    {
      question: "Welche Prozesse eignen sich am besten für Automatisierung?",
      answer:
        "Ideale Kandidaten sind repetitive, regelbasierte Prozesse mit klaren Entscheidungskriterien. Dazu gehören Datenverarbeitung, Rechnungsbearbeitung, Bestellabwicklung, Reporterstellung und Kundenkommunikation. Wir helfen Ihnen dabei, die Prozesse mit dem höchsten Automatisierungspotenzial zu identifizieren.",
    },
    {
      question:
        "Wie lange dauert die Implementierung einer Automatisierungslösung?",
      answer:
        "Die Implementierungszeit variiert je nach Komplexität: Einfache Automatisierungen können in 2-4 Wochen umgesetzt werden, während komplexe Enterprise-Lösungen 2-6 Monate benötigen. Wir arbeiten agil und liefern erste Ergebnisse oft schon nach wenigen Wochen.",
    },
    {
      question: "Welchen ROI kann ich von Automatisierung erwarten?",
      answer:
        "Die meisten unserer Kunden sehen einen ROI zwischen 200-500% im ersten Jahr. Typische Einsparungen liegen bei 40-70% der Prozesskosten durch reduzierte Arbeitszeit, weniger Fehler und verbesserte Effizienz. Der genaue ROI hängt vom automatisierten Prozess ab.",
    },
    {
      question: "Wie sicher sind automatisierte Prozesse?",
      answer:
        "Sicherheit hat höchste Priorität. Wir implementieren Verschlüsselung, Zugriffskontrollen, Audit-Trails und Backup-Mechanismen. Automatisierte Prozesse sind oft sicherer als manuelle, da sie menschliche Fehler eliminieren und konsistente Sicherheitsrichtlinien durchsetzen.",
    },
    {
      question: "Was passiert, wenn sich meine Geschäftsprozesse ändern?",
      answer:
        "Unsere Automatisierungslösungen sind flexibel und anpassbar gestaltet. Änderungen können meist schnell und kostengünstig implementiert werden. Wir bieten auch Wartungs- und Anpassungsservices, um sicherzustellen, dass Ihre Automatisierung mit Ihrem Unternehmen mitwächst.",
    },
  ];

  return (
    <PageContainer>
      <SEO
        title="Prozessautomatisierung für Unternehmen"
        description="Intelligente Automatisierung von Geschäftsprozessen: Kosten senken, Effizienz steigern, Fehler vermeiden. Byte Forge entwickelt maßgeschneiderte Automatisierungslösungen."
        path="/automatisierung"
        keywords="Prozessautomatisierung, Automatisierung Unternehmen, Workflow-Automatisierung, RPA, Geschäftsprozesse automatisieren"
      />
      <Container>
        <SectionTitle>
          <Trans>
            Automatisierung für <span>Ihr Unternehmen</span>
          </Trans>
        </SectionTitle>
        <SectionSubtitle>
          Transformieren Sie Ihre Geschäftsprozesse mit intelligenter
          Automatisierung. Wir entwickeln maßgeschneiderte Lösungen, die
          Effizienz steigern, Kosten senken und Ihren Mitarbeitern Zeit für
          wertschöpfende Tätigkeiten verschaffen.
        </SectionSubtitle>
      </Container>

      <ContentSection>
        <Container>
          <SectionTitleQuestion>
            Warum <span>Automatisierung</span>?
          </SectionTitleQuestion>
          <Grid>
            {advantages.map((advantage, index) => (
              <Card key={index}>
                <IconWrapper>{advantage.icon}</IconWrapper>
                <CardTitle>{advantage.title}</CardTitle>
                <CardDescription>{advantage.description}</CardDescription>
              </Card>
            ))}
          </Grid>
        </Container>
      </ContentSection>

      <ProcessSection>
        <Container>
          <SectionTitle>
            Unser <span>Automatisierungsprozess</span>
          </SectionTitle>
          <ProcessGrid>
            {process.map((step, index) => (
              <ProcessCard key={index}>
                <ProcessNumber>{index + 1}</ProcessNumber>
                <ProcessTitle>{step.title}</ProcessTitle>
                <ProcessDescription>{step.description}</ProcessDescription>
              </ProcessCard>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>

      <BenefitsSection>
        <Container>
          <SectionTitle>
            Ihre <span>Vorteile</span>
          </SectionTitle>
          <BenefitsList>
            {benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitIcon>
                  <CheckCircleIcon />
                </BenefitIcon>
                <BenefitText>
                  <h5>{benefit.title}</h5>
                  <p>{benefit.description}</p>
                </BenefitText>
              </BenefitItem>
            ))}
          </BenefitsList>
        </Container>
      </BenefitsSection>

      <TechnologySection>
        <Container>
          <SectionTitle>
            Unsere <span>Automatisierungs-Technologien</span>
          </SectionTitle>
          <TechnologyGrid>
            {technologies.map((tech, index) => (
              <TechCard key={index}>
                <TechIcon>{tech.icon}</TechIcon>
                <TechName>{tech.name}</TechName>
                <TechDescription>{tech.description}</TechDescription>
              </TechCard>
            ))}
          </TechnologyGrid>
        </Container>
      </TechnologySection>

      <CaseStudySection>
        <Container>
          <SectionTitle>
            Erfolgreiche <span>Automatisierungsprojekte</span>
          </SectionTitle>
          <CaseStudyGrid>
            {caseStudies.map((caseStudy, index) => (
              <CaseStudyCard key={index}>
                <CaseStudyTitle>{caseStudy.title}</CaseStudyTitle>
                <CaseStudyText>{caseStudy.description}</CaseStudyText>
                <CaseStudyResult>
                  <h5>{caseStudy.result.title}</h5>
                  <p>{caseStudy.result.description}</p>
                </CaseStudyResult>
              </CaseStudyCard>
            ))}
          </CaseStudyGrid>
        </Container>
      </CaseStudySection>

      <ServiceTypesSection>
        <Container>
          <SectionTitle>
            Unsere <span>Automatisierungsservices</span>
          </SectionTitle>
          <ServiceTypeGrid>
            {serviceTypes.map((service, index) => (
              <ServiceTypeCard key={index}>
                <ServiceTypeTitle>{service.title}</ServiceTypeTitle>
                <ServiceTypeDescription>
                  {service.description}
                </ServiceTypeDescription>
                <ServiceTypeFeatures>
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>{feature}</li>
                  ))}
                </ServiceTypeFeatures>
              </ServiceTypeCard>
            ))}
          </ServiceTypeGrid>
        </Container>
      </ServiceTypesSection>

      <FAQSection>
        <Container>
          <SectionTitle>
            Häufig gestellte <span>Fragen</span>
          </SectionTitle>
          <FAQContainer>
            {faqs.map((faq, index) => (
              <FAQItem key={index}>
                <FAQQuestion>{faq.question}</FAQQuestion>
                <FAQAnswer>{faq.answer}</FAQAnswer>
              </FAQItem>
            ))}
          </FAQContainer>
        </Container>
      </FAQSection>

      <Container>
        <CTAContainer>
          <SectionTitleQuestion>
            <Trans>
              Bereit für <span>die Automatisierung?</span>
            </Trans>
          </SectionTitleQuestion>
          <SectionSubtitle>
            Lassen Sie uns gemeinsam Ihre Potenziale identifizieren und eine
            maßgeschneiderte Automatisierungsstrategie für Ihr Unternehmen
            entwickeln.
            <CTAButton smooth to="/#contact">
              Kostenloses Erstgespräch
            </CTAButton>
          </SectionSubtitle>
        </CTAContainer>
      </Container>

    </PageContainer>
  );
};

export default Automatisierung;
